import auth0 from 'auth0-js'
import { logger } from '../../lib/logging'
import storage from '../../lib/localstorage.jsx'
import { Authentication } from './authentication'
import { InvalidCredentials } from './invalidCredentials'
import { AccountLocked } from './accountLocked'
import { SessionNotAuthenticated } from './sessionNotAuthenticated'
import jwtDecode from 'jwt-decode'
import Acl from '../../lib/acl'

export class Auth0Authentication extends Authentication {
  constructor (app) {
    super(app)
    const { frontendDomain, frontendClientId, frontendRedirectUri, connectionName, audience } = app.config.auth0

    this.auth0 = new auth0.WebAuth({
      domain: frontendDomain,
      clientID: frontendClientId,
      redirectUri: frontendRedirectUri || this.getDefaultRedirectURL(),
      realm: connectionName,
      audience,
      responseType: 'token',
      scope: 'openid profile email'
    })
  }

  /**
   * Init the authentication strategy.
   *
   * @returns {Promise}
   */
  async init () {
    try {
      let accessToken

      if (window.location.hash) {
        logger.debug('Parsing hash from Auth0...')
        const data = await this.parseHash()
        accessToken = data.accessToken
      } else {
        logger.debug('Checking for active session in Auth0...')
        const data = await this.checkForActiveSession()
        accessToken = data.accessToken
      }

      const { user } = await this.app.feathers.authenticate({
        strategy: 'auth0',
        access_token: accessToken
      })
      logger.debug('Active session found for user:', user)
      const teamId = this.app.teamContext.getActiveTeamIdFromStorage()
      logger.debug('Active team id in storage:', teamId)
      this.setPermissions(accessToken)
      const result = await this.afterLogin(user, teamId)
      return result
    } catch (error) {
      if (error.code === 'login_required' || error.code === 'consent_required' || error.error === 'invalid_token') {
        logger.warn(error)
        throw new SessionNotAuthenticated()
      } else {
        throw error
      }
    }
  }

  /**
   * Login to auth0 with email and password.
   *
   * @param {object} credentials
   * @returns {Promise}
   */
  login ({ email, password, isSignupFlow }) {
    logger.debug('Logging in with auth0...', { email })
    return new Promise((resolve, reject) => {
      const callback = (error, data) => {
        if (error) {
          if (error.code === 'access_denied') {
            return reject(new InvalidCredentials())
          }
          if (error.code === 'too_many_attempts') {
            return reject(new AccountLocked())
          }

          return reject(error)
        }

        const { accessToken } = data

        return resolve(this.app.feathers.authenticate({
          strategy: 'auth0',
          access_token: accessToken
        }).then(({ user }) => {
          logger.debug('Logged in successfully', { user })
          this.setPermissions(accessToken)
          return this.afterLogin(user, null, isSignupFlow)
        }))
      }

      const { hostname } = window.location
      const useRedirectLogin = hostname === 'localhost' || hostname === '127.0.0.1'

      // Auth0 will *always* require consent for applications running on localhost, even if they
      // are first party applications. Ideally we'd like to use the `popup.loginWithCredentials`
      // since it will do a silent authentication without a redirect, but unfortunately that's
      // not an option for local environment, so we will use the login with redirect there.
      if (useRedirectLogin) {
        logger.debug('Using login')
        this.auth0.login({
          email,
          password
        }, callback)
      } else {
        logger.debug('Using popup login')
        this.auth0.popup.loginWithCredentials({
          email,
          password
        }, callback)
      }
    })
  }

  /**
   * Logout from auth0.
   *
   * @returns {Promise}
   */
  logout () {
    return new Promise((resolve, reject) => {
      storage.clear()
      this.auth0.logout({
        returnTo: this.app.config.auth0.frontendRedirectUri || this.getDefaultRedirectURL()
      })
    })
  }

  refresh () {
    // TODO
    // return this.app.feathers.reAuthenticate()
  }

  /**
   * Check for an active auth0 session.
   *
   * @returns {Promise}
   */
  checkForActiveSession () {
    return new Promise((resolve, reject) => {
      this.auth0.checkSession({
        timeout: 5000
      }, (error, result) => {
        if (error) {
          return reject(error)
        }

        return resolve(result)
      })
    })
  }

  /**
   * Parse information from Auth0 in the hash of the location and return it.
   *
   * @returns {Promise}
   */
  parseHash () {
    return new Promise((resolve, reject) => {
      this.auth0.parseHash({ hash: window.location.hash }, (error, result) => {
        if (error) {
          window.location.hash = ''
          return reject(error)
        }

        return resolve(result)
      })
    })
  }

  /**
   * Decodes the JWT and sets the permissions from auth0
   *
   * @returns {Promise}
   */
  setPermissions (accessToken) {
    try {
      const { permissions } = jwtDecode(accessToken)
      Acl.setPermissions(permissions)
    } catch (e) {
      return null
    }
  }

  /**
   * Returns default redirect URL for auth0
   *
   * @returns string
   */
  getDefaultRedirectURL () {
    const { origin } = window.location
    return `${origin}/login`
  }
}
